import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Coffee, Plus, Minus, ShoppingCart, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import orderService from '../services/orderService';
import { themeService, getGridClasses } from '../services/themeService';
import ProductCard from '../components/ProductCard';
import { getRandomSlogan } from '../data/themes';
import toast from 'react-hot-toast';
import { getProductImageUrl } from '../utils/imageUtils';

const ShopMenu = () => {
  const { t } = useTranslation();
  const { slug, tableNumber } = useParams();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [theme, setTheme] = useState(themeService.getCurrentTheme());
  const [customization, setCustomization] = useState(themeService.getCustomization());

  const fetchShopData = useCallback(async () => {
    if (hasAttemptedFetch) {
  logger.debug('ShopMenu: Skipping fetch - already attempted');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setHasAttemptedFetch(true);
      
      const shopResponse = await api.get(`/shop/${slug}`);
      const shopData = shopResponse.data;
      setShop(shopData);
      // If server provides a global theme for this shop, apply it
      if (shopData?.menuThemeId) {
        try {
          themeService.applyTheme(shopData.menuThemeId);
          if (shopData.menuCustomization) {
            // Persist customization locally to drive UI controls
            const parsed = typeof shopData.menuCustomization === 'string' 
              ? JSON.parse(shopData.menuCustomization) 
              : shopData.menuCustomization;
            if (parsed && parsed.themeId) {
              themeService.saveCustomization(parsed);
            } else {
              // Fallback: just save theme id
              themeService.saveCustomization({ themeId: shopData.menuThemeId });
            }
          } else {
            themeService.saveCustomization({ themeId: shopData.menuThemeId });
          }
        } catch (e) {
  logger.warn('Failed to apply shop theme from server:', e);
        }
      }
      
      const productsResponse = await api.get(`/shop/${slug}/products`);
      const productsData = productsResponse.data;
      setProducts(productsData);
    } catch (error) {
      setError(error);
      toast.error(t('menu.menuUnavailable'));
    } finally {
      setLoading(false);
    }
  }, [slug, hasAttemptedFetch, t]);

  useEffect(() => {
    fetchShopData();
  }, [fetchShopData]);

  // Initialize default theme and set up theme listener
  useEffect(() => {
    themeService.initializeDefaultTheme();
    setTheme(themeService.getCurrentTheme());
    setCustomization(themeService.getCustomization());

    // Set up theme change listener
    const handleThemeChange = (newTheme, newCustomization) => {
      setTheme(newTheme);
      setCustomization(newCustomization);
    };

    themeService.addListener(handleThemeChange);

    return () => {
      themeService.removeListener(handleThemeChange);
    };
  }, []);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} ${t('menu.addToCart')}`);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.id === productId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error(t('menu.yourCartEmpty'));
      return;
    }

    if (isSubmittingOrder) {
      toast.error(t('menu.orderAlreadyBeingSubmitted'));
      return;
    }

    try {
      setIsSubmittingOrder(true);
      
      const orderData = {
        tableNumber: parseInt(tableNumber),
        items: cart.map(item => ({
          productId: item.id,
          quantity: item.quantity
        }))
      };

  logger.debug('Submitting order:', orderData);
      const result = await orderService.createPublicOrder(slug, orderData);
      
      if (result.success) {
  logger.info('Order submitted successfully:', result.data);
        setOrderSubmitted(true);
        setCart([]);
        toast.success(t('menu.orderSubmittedSuccessfully'));
      } else {
  logger.error('Order submission failed:', result.error);
        toast.error(result.error || t('menu.failedToSubmitOrder'));
      }
    } catch (error) {
  logger.error('Failed to submit order:', error);
      toast.error(t('menu.failedToSubmitOrder'));
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: theme.colors.background.includes('gradient') ? theme.colors.background : theme.colors.background }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-6"
            style={{ borderColor: theme.colors.primary }}
          />
          <p 
            className="text-xl"
            style={{ color: theme.colors.textSecondary }}
          >
            {t('menu.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: theme.colors.background.includes('gradient') ? theme.colors.background : theme.colors.background }}
      >
        <div className="text-center max-w-sm">
          <AlertCircle 
            className="w-20 h-20 mx-auto mb-6" 
            style={{ color: theme.colors.error }}
          />
          <h2 
            className="text-3xl font-bold mb-4"
            style={{ color: theme.colors.text }}
          >
            {t('menu.menuNotFound')}
          </h2>
          <p 
            className="text-lg"
            style={{ color: theme.colors.textSecondary }}
          >
            {t('menu.menuNotAvailable')}
          </p>
        </div>
      </div>
    );
  }

  if (orderSubmitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: theme.colors.background.includes('gradient') ? theme.colors.background : theme.colors.background }}
      >
        <div className="text-center max-w-sm">
          <CheckCircle 
            className="w-24 h-24 mx-auto mb-6 animate-bounce" 
            style={{ color: theme.colors.success }}
          />
          <h2 
            className="text-4xl font-bold mb-4"
            style={{ color: theme.colors.text }}
          >
            {t('menu.orderConfirmed')}
          </h2>
          <p 
            className="text-xl mb-8"
            style={{ color: theme.colors.textSecondary }}
          >
            {t('menu.orderBeingPreparedCoffee')}
          </p>
          <div 
            className="rounded-xl p-6 mb-8"
            style={{ backgroundColor: theme.colors.surface }}
          >
            <p 
              className="text-sm mb-2"
              style={{ color: theme.colors.textSecondary }}
            >
              {t('menu.table')} {tableNumber}
            </p>
            <p 
              className="text-2xl font-semibold"
              style={{ color: theme.colors.text }}
            >
              {shop?.name}
            </p>
          </div>
          <button
            onClick={() => setOrderSubmitted(false)}
            className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 w-full border-2 shadow-lg"
            style={{ 
              backgroundColor: theme.colors.primary,
              color: '#FFFFFF',
              borderColor: theme.colors.primary,
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.secondary;
              e.target.style.borderColor = theme.colors.secondary;
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.borderColor = theme.colors.primary;
              e.target.style.transform = 'scale(1)';
            }}
          >
            {t('menu.placeAnotherOrder')}
          </button>
        </div>
      </div>
    );
  }

  const filteredProducts = products.filter(product => 
    selectedCategory === 'all' || product.category === selectedCategory
  );

  const categories = ['all', ...new Set(products.map(p => p.category))];

  const renderProductCard = (product) => {
    return (
      <ProductCard
        key={product.id}
        product={product}
        theme={theme}
        customization={theme?.layout || customization}
        onAddToCart={addToCart}
        isPreview={false}
      />
    );
  };

  return (
    <div 
      className="min-h-screen"
      style={{ 
        background: theme.colors.background.includes('gradient') ? theme.colors.background : theme.colors.background,
        fontFamily: theme.typography.fontFamily
      }}
    >
      {/* Beautiful Header with Shop Name and Slogan */}
      <header 
        className="relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
        }}
      >
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="relative px-4 py-8">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div 
                className="p-4 rounded-full"
                style={{ backgroundColor: `${theme.colors.surface}20` }}
              >
                <Coffee 
                  className="w-12 h-12" 
                  style={{ color: theme.colors.surface }}
                />
              </div>
            </div>
            
            <h1 
              className="font-bold leading-tight mb-2"
              style={{ 
                color: theme.colors.surface,
                fontFamily: theme.typography.headingFont,
                fontSize: `${theme.typography.headingSize + 8}px`
              }}
            >
              {shop?.name || 'CoffeeCloud'}
            </h1>
            
            <p 
              className="text-lg mb-4 opacity-90"
              style={{ 
                color: theme.colors.surface,
                fontFamily: theme.typography.bodyFont
              }}
            >
{getRandomSlogan()}
            </p>
            
            <div 
              className="inline-flex items-center space-x-3 px-6 py-3 rounded-full"
              style={{ 
                backgroundColor: `${theme.colors.surface}20`,
                backdropFilter: 'blur(10px)'
              }}
            >
              <span 
                className="text-sm font-medium"
                style={{ 
                  color: theme.colors.surface,
                  fontFamily: theme.typography.bodyFont
                }}
              >
                {t('menu.table')} {tableNumber}
              </span>
              
              <button 
                onClick={() => setShowCart(true)}
                className="relative flex items-center space-x-3 px-6 py-4 rounded-full transition-all duration-200 hover:scale-110 border-3 shadow-2xl cart-button"
                style={{ 
                  backgroundColor: '#FFFFFF',
                  color: '#1F2937',
                  borderColor: '#1F2937',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 0 0 2px rgba(255,255,255,0.1)'
                }}
              >
                <ShoppingCart className="w-6 h-6 font-bold" />
                <div className="flex flex-col items-start">
                  <span className="text-xs font-medium opacity-80">{t('menu.cart')}</span>
                  <span className="text-lg font-bold leading-none">
                    {cart.length} {t('menu.cartItems')}
                    {cart.length > 0 && (
                      <span className="text-sm font-medium opacity-70 ml-1">
                        • ${cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
                      </span>
                    )}
                  </span>
                </div>
                {cart.length > 0 && (
                  <span 
                    className="absolute -top-3 -right-3 bg-red-500 text-white text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-white shadow-lg animate-pulse"
                  >
                    {cart.reduce((total, item) => total + item.quantity, 0)}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Category Filter - Horizontal Scroll */}
      {(theme?.layout?.showCategories !== undefined ? theme.layout.showCategories : (customization?.showCategories !== undefined ? customization.showCategories : true)) && (
        <div className="px-4 py-6">
          <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => {
              const count = category === 'all' 
                ? products.length 
                : products.filter(p => p.category === category).length;
              
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-4 rounded-xl text-lg font-bold transition-all duration-200 whitespace-nowrap flex-shrink-0 border-2 ${
                    selectedCategory === category
                      ? 'shadow-lg scale-105'
                      : 'hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: selectedCategory === category ? theme.colors.primary : theme.colors.surface,
                    color: selectedCategory === category ? '#FFFFFF' : theme.colors.textSecondary,
                    fontFamily: theme.typography.headingFont,
                    borderColor: selectedCategory === category ? theme.colors.primary : theme.colors.border,
                    textShadow: selectedCategory === category ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category) {
                      e.target.style.backgroundColor = theme.colors.secondary;
                      e.target.style.color = '#FFFFFF';
                      e.target.style.borderColor = theme.colors.secondary;
                      e.target.style.textShadow = '1px 1px 2px rgba(0,0,0,0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category) {
                      e.target.style.backgroundColor = theme.colors.surface;
                      e.target.style.color = theme.colors.textSecondary;
                      e.target.style.borderColor = theme.colors.border;
                      e.target.style.textShadow = 'none';
                    }
                  }}
                >
                  {category === 'all' ? t('menu.all') : category}
                  <span 
                    className="ml-2 text-sm opacity-75"
                    style={{ color: 'inherit' }}
                  >
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Products Grid - Mobile Optimized */}
      <div className="px-4 pb-32">
        {theme?.layout?.layoutStyle === 'featured' ? (
          <div className="space-y-6">
            {filteredProducts.length > 0 && (
              <ProductCard
                key={filteredProducts[0].id}
                product={filteredProducts[0]}
                theme={theme}
                customization={theme?.layout || customization}
                onAddToCart={addToCart}
                isPreview={false}
                isFeatured={true}
              />
            )}
            <div className="grid grid-cols-2 gap-4">
              {filteredProducts.slice(1).map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  theme={theme}
                  customization={{...theme?.layout || customization, productCardStyle: 'vertical'}}
                  onAddToCart={addToCart}
                  isPreview={false}
                />
              ))}
            </div>
          </div>
        ) : (
          <div 
            className={`gap-6 ${getGridClasses(theme, customization)}`}
          >
            {filteredProducts.map(renderProductCard)}
          </div>
        )}
      </div>

      {/* Floating Cart Button - Always Visible */}
      <button
        onClick={() => setShowCart(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-16 h-16 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 shadow-2xl border-4 floating-cart-button"
        style={{
          backgroundColor: '#FFFFFF',
          borderColor: '#1F2937',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4), 0 0 0 2px rgba(255,255,255,0.1)'
        }}
      >
        <div className="relative">
          <ShoppingCart className="w-8 h-8 text-[#1F2937] font-bold" />
          {cart.length > 0 && (
            <span 
              className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white shadow-lg animate-bounce"
            >
              {cart.reduce((total, item) => total + item.quantity, 0)}
            </span>
          )}
        </div>
      </button>

      {/* Mobile Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
          <div 
            className="absolute bottom-0 left-0 right-0 rounded-t-3xl max-h-[80vh] overflow-hidden"
            style={{ backgroundColor: theme.colors.surface }}
          >
            {/* Cart Header */}
            <div 
              className="flex items-center justify-between p-6 border-b"
              style={{ borderColor: theme.colors.border }}
            >
              <div>
                <h2 
                  className="text-2xl font-bold"
                  style={{ 
                    color: theme.colors.text,
                    fontFamily: theme.typography.headingFont
                  }}
                >
                  {t('menu.yourOrder')}
                </h2>
                <p 
                  style={{ 
                    color: theme.colors.textSecondary,
                    fontFamily: theme.typography.bodyFont
                  }}
                >
                  {t('menu.table')} {tableNumber}
                </p>
              </div>
              <button
                onClick={() => setShowCart(false)}
                className="p-3 rounded-full transition-colors"
                style={{ backgroundColor: theme.colors.border }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.error;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.border;
                }}
              >
                <X 
                  className="w-6 h-6" 
                  style={{ color: theme.colors.text }}
                />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart 
                    className="w-20 h-20 mx-auto mb-6" 
                    style={{ color: theme.colors.textSecondary }}
                  />
                  <h3 
                    className="text-2xl font-bold mb-4"
                    style={{ 
                      color: theme.colors.text,
                      fontFamily: theme.typography.headingFont
                    }}
                  >
                    {t('menu.cartIsEmpty')}
                  </h3>
                  <p 
                    className="text-lg"
                    style={{ 
                      color: theme.colors.textSecondary,
                      fontFamily: theme.typography.bodyFont
                    }}
                  >
                    {t('menu.tapProductToAddOrder')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div 
                      key={item.id} 
                      className="rounded-2xl p-4 border"
                      style={{ 
                        backgroundColor: theme.colors.background,
                        borderColor: theme.colors.border
                      }}
                    >
                      <div className="flex items-center space-x-4 mb-4">
                        {customization.showImages && item.imageUrl && (
                          <img
                            src={getProductImageUrl(item.imageUrl)}
                            alt={item.name}
                            className="w-16 h-16 rounded-xl object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <h4 
                            className="text-xl font-bold"
                            style={{ 
                              color: theme.colors.text,
                              fontFamily: theme.typography.headingFont
                            }}
                          >
                            {item.name}
                          </h4>
                          <p 
                            className="text-lg font-bold"
                            style={{ color: theme.colors.accent }}
                          >
                            ${item.price}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                          style={{ backgroundColor: theme.colors.error }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc2626';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = theme.colors.error;
                          }}
                        >
                          <X 
                            className="w-5 h-5" 
                            style={{ color: theme.colors.text }}
                          />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                            style={{ 
                              backgroundColor: item.quantity <= 1 ? theme.colors.border : theme.colors.secondary
                            }}
                          >
                            <Minus 
                              className="w-6 h-6" 
                              style={{ color: theme.colors.text }}
                            />
                          </button>
                          <span 
                            className="text-2xl font-bold min-w-[3rem] text-center"
                            style={{ 
                              color: theme.colors.text,
                              fontFamily: theme.typography.headingFont
                            }}
                          >
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                            style={{ backgroundColor: theme.colors.success }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#059669';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = theme.colors.success;
                            }}
                          >
                            <Plus 
                              className="w-6 h-6" 
                              style={{ color: theme.colors.text }}
                            />
                          </button>
                        </div>
                        <div className="text-right">
                          <p 
                            className="text-2xl font-bold"
                            style={{ 
                              color: theme.colors.accent,
                              fontFamily: theme.typography.headingFont
                            }}
                          >
                            ${(item.quantity * item.price).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            <div 
              className="border-t p-6"
              style={{ 
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.background
              }}
            >
              {cart.length > 0 && (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <span 
                      className="text-xl"
                      style={{ 
                        color: theme.colors.textSecondary,
                        fontFamily: theme.typography.bodyFont
                      }}
                    >
                      {t('menu.total')}:
                    </span>
                    <span 
                      className="text-3xl font-bold"
                      style={{ 
                        color: theme.colors.accent,
                        fontFamily: theme.typography.headingFont
                      }}
                    >
                      ${getTotalPrice().toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={submitOrder}
                    disabled={isSubmittingOrder}
                    className="w-full py-5 font-bold text-xl rounded-2xl transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: isSubmittingOrder ? theme.colors.textSecondary : theme.colors.primary,
                      color: theme.colors.text,
                      fontFamily: theme.typography.headingFont
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmittingOrder) {
                        e.target.style.backgroundColor = theme.colors.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmittingOrder) {
                        e.target.style.backgroundColor = theme.colors.primary;
                      }
                    }}
                  >
                    {isSubmittingOrder ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: theme.colors.text }}></div>
                        <span>{t('menu.submitting')}</span>
                      </div>
                    ) : (
                      t('menu.submitOrder')
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopMenu;
